class GiftMessageForm extends BaseMultishipForm {
  constructor() {
    super();
    this.maxCharacters = 250;
  }

  determineInitialState() {
    this.loadGiftMessageData();
    if (!this.isShippingComplete()) {
      return 'shipping_required';
    }
    if (this.giftMessageData.includeGiftMessage == 'yes') {
      return this.giftMessageData.message ? 'filled' : 'form'; 
    }
    if (this.giftMessageData.includeGiftMessage == 'no') {
      return 'filled';
    }
    return 'choice';
  }

  getDefaultState() {
    return 'shipping_required';
  }

  loadGiftMessageData() {
    this.giftMessageData = {
      message: this.data.giftMessage || this.findPropertyValue('Gift message') || '',
      includeGiftMessage: this.data.includeGiftMessage || this.findPropertyValue('Include gift message') || ''
    };

    this.shippingData = {
      shippingMethod: this.data.shippingMethod || this.findPropertyValue('Shipping method'),
      shippingPrice: this.data.shippingPrice || this.findPropertyValue('Shipping price'),
      requestedShipDate: this.data.requestedShipDate || this.findPropertyValue('Ship on')
    };
  }

  findPropertyValue(key) {
    const property = this.properties.find(prop => prop[0] === key);
    return property ? property[1] : '';
  }

  isShippingComplete() {
    return this.shippingData &&
      (this.shippingData.shippingMethod || this.shippingData.shippingPrice || this.shippingData.requestedShipDate);
  }

  getTemplate() {
    switch (this.state) {
      case 'shipping_required':
        return this.getShippingRequiredTemplate();
      case 'choice':
        return this.getChoiceTemplate();
      case 'form':
        return this.getFormTemplate();
      case 'filled':
        return this.getFilledTemplate();
      default:
        console.error('Invalid state:', this.state);
        return '';
    }
  }

  getChoiceTemplate() {
    return `
    ${this.baseMultishipFormStyles()}
    <div class="multiship__section">
      <div class="multiship__header">
        <h4 id="gift-message-heading-${this.lineItemId}">Gift Message</h4>
      </div>
      <div class="gift-message-choice" role="group" aria-labelledby="gift-message-heading-${this.lineItemId}">
        <button type="button" class="button button--full-width" data-element="include-message-btn" style="margin-bottom: 1rem;">Include Message</button>
        <button type="button" class="button button--full-width button--secondary" data-element="no-message-btn">No Message</button>
      </div>
    </div>
  `;
  }

  getUnfilledTemplate() {
    return `
      ${this.baseMultishipFormStyles()}
      <button type="button" class="multiship__button" data-element="show-form-btn" aria-label="Add a gift message">
        ${this.iconCirclePlus()}
        Add a gift message
      </button>
    `;
  }

  getShippingRequiredTemplate() {
    return `
      ${this.baseMultishipFormStyles()}
      <div class="multiship__button multiship__button--disabled" aria-live="polite">
        ${this.iconCirclePlus("gray")}
        <div><strong>Add Gift Message:</strong> Please complete shipping information first.</div>
      </div>
    `;
  }

  getFormTemplate() {
    return `
      ${this.baseMultishipFormStyles()}
      <div class="multiship__section">
        <div class="multiship__header">
          <h4 id="gift-message-form-heading-${this.lineItemId}">Gift Message</h4>
        </div>
        <div class="gift-message-form" data-form-id="${this.lineItemId}" role="form" aria-labelledby="gift-message-form-heading-${this.lineItemId}">
          <div class="gift-message-form__main">
            <label for="gift-message-${this.lineItemId}" class="sr-only">Gift Message</label>
            <textarea
              id="gift-message-${this.lineItemId}"
              data-element="message"
              maxlength="${this.maxCharacters}"
              rows="4"
              aria-describedby="character-count-${this.lineItemId}"
            >${this.giftMessageData.message}</textarea>
            <div data-error="message" class="error-message" aria-live="polite"></div>
            <div data-element="character-count" id="character-count-${this.lineItemId}" class="helper" aria-live="polite">${this.maxCharacters - this.giftMessageData.message.length} characters remaining</div>
          </div>
          <div class="multiship__actions">
            <button type="button" data-element="save-message-btn" class="button">Save <span class="sr-only">Gift Message</span></button>
            <button type="button" data-element="cancel-message-btn" class="link-button">Cancel</button>
            <button type="button" data-element="remove-message-btn" class="link-button link-button--danger">Remove</button>
          </div>
        </div>
      </div>
    `;
  }

  getFilledTemplate() {
    return `
    ${this.baseMultishipFormStyles()}
    <div class="multiship__section">
      <div class="multiship__header">
        <h4 id="gift-message-filled-heading-${this.lineItemId}">Gift Message</h4>
        <button type="button" class="link-button" data-element="edit-message-btn" aria-label="Edit gift message">Edit</button>
      </div>
      <p aria-labelledby="gift-message-filled-heading-${this.lineItemId}">${this.giftMessageData.includeGiftMessage === 'no' ? 'No gift message' : this.giftMessageData.message}</p>
    </div>
  `;
  }

  attachEventListeners() {
    if (this.state === 'choice') {
      const includeMessageBtn = this.shadowRoot.querySelector('[data-element="include-message-btn"]');
      const noMessageBtn = this.shadowRoot.querySelector('[data-element="no-message-btn"]');

      includeMessageBtn.addEventListener('click', () => this.handleIncludeMessage());
      noMessageBtn.addEventListener('click', () => this.handleNoMessage());
    } else if (this.state === 'unfilled') {
      const showFormBtn = this.shadowRoot.querySelector('[data-element="show-form-btn"]');
      showFormBtn.addEventListener('click', () => this.showForm());
    } else if (this.state === 'form') {
      const messageTextarea = this.shadowRoot.querySelector('[data-element="message"]');
      const saveBtn = this.shadowRoot.querySelector('[data-element="save-message-btn"]');
      const removeBtn = this.shadowRoot.querySelector('[data-element="remove-message-btn"]');
      const cancelBtn = this.shadowRoot.querySelector('[data-element="cancel-message-btn"]');

      messageTextarea.addEventListener('input', () => this.updateCharacterCount());

      saveBtn.addEventListener('click', () => this.handleSubmit());
      removeBtn.addEventListener('click', () => this.handleRemove());
      cancelBtn.addEventListener('click', () => this.handleCancel());

      messageTextarea.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          this.handleSubmit();
        }
      });
    } else if (this.state === 'filled') {
      const editBtn = this.shadowRoot.querySelector('[data-element="edit-message-btn"]');
      editBtn.addEventListener('click', () => {
        if (this.giftMessageData.includeGiftMessage == 'yes') {
          this.state = 'form';
        } else {
          this.state = 'choice';
        }
        this.render();
      });
    }
  }

  async handleIncludeMessage() {
    this.giftMessageData.includeGiftMessage = 'yes';
    this.state = 'form';
    this.render();
  }

  async handleNoMessage() {
    this.giftMessageData.includeGiftMessage = 'no';
    this.giftMessageData.message = '';
    await this.updateIncludeGiftMessage('no');
    this.state = 'filled';
    this.render();
  }

  async updateIncludeGiftMessage(value) {
    try {
      const updatedData = {
        ...this.data,
        includeGiftMessage: value,
        giftMessage: value === 'no' ? '' : this.giftMessageData.message
      };

      this.saveDataProperty(updatedData);
      this.updateProperty('Include gift message', value);

      if (value === 'no') {
        this.removeProperty('Gift message');
      }

      await this.updateCart({});
    } catch (error) {
      console.error('Error updating include gift message:', error);
    }
  }

  updateCharacterCount() {
    const messageTextarea = this.shadowRoot.querySelector('[data-element="message"]');
    const characterCount = this.shadowRoot.querySelector('[data-element="character-count"]');
    const remainingChars = this.maxCharacters - messageTextarea.value.length;
    characterCount.textContent = `${remainingChars} characters remaining`;
  }
  
  validateForm() {
    this.clearErrors();
    let isValid = true;
  
    if (this.giftMessageData.includeGiftMessage === 'yes') {
      const messageTextarea = this.shadowRoot.querySelector('[data-element="message"]');
      const message = messageTextarea.value.trim();

      if (!message) {
        this.showError('message', 'Please enter a gift message.');
        isValid = false;
      } else if (this.containsEmojis(message)) {
        this.showError('message', 'Please remove emojis from your gift message.');
        isValid = false;
      }
    }
  
    return isValid;
  }
  
  containsEmojis(text) {
    const emojiRegex = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu;
    return emojiRegex.test(text);
  }
  
  showError(field, message) {
    const errorElement = this.shadowRoot.querySelector(`[data-error="${field}"]`);
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.add('error-message');
      errorElement.setAttribute('role', 'alert');
    }
  }
  
  clearErrors() {
    const errorElements = this.shadowRoot.querySelectorAll('[data-error]');
    errorElements.forEach(element => {
      element.textContent = '';
      element.classList.remove('error-message');
      element.removeAttribute('role');
    });
  }

  async handleSubmit() {
    if (!this.validateForm()) {
      this.focusFirstError();
      return;
    }
  
    await super.handleSubmit();
    const form = this.shadowRoot.querySelector(`[data-form-id="${this.lineItemId}"]`);
    const messageTextarea = form.querySelector('[data-element="message"]');
    const message = messageTextarea.value.trim();

    try {
      const updatedData = {
        ...this.data,
        giftMessage: message,
        includeGiftMessage: 'yes'
      };

      this.saveDataProperty(updatedData);
      this.updateProperty('Include gift message', 'yes');

      if (message) {
        this.updateProperty('Gift message', message);
      }

      const result = await this.updateCart({});

      this.giftMessageData = { message, includeGiftMessage: 'yes' };
      this.state = 'filled';
      this.render();

      this.showSuccessMessage('Form submitted successfully');
    } catch (error) {
      console.error('Error updating cart with gift message:', error);
      this.showErrorMessage('An error occurred. Please try again.');
    }
  }
  
  async handleRemove() {
    try {
      const updatedData = {
        ...this.data,
        giftMessage: '',
        includeGiftMessage: 'no'
      };
  
      this.saveDataProperty(updatedData);
      
      // Remove the 'Gift message' property
      this.removeProperty('Gift message');
  
      const result = await this.updateCart({});
  
      // Update our local data
      this.giftMessageData = { message: '' };
  
      // Force a re-render to the unfilled state
      this.state = 'unfilled';
      this.render();
  
    } catch (error) {
      console.error('Error removing gift message:', error);
    }
  }
  
  // Add this method to the class if it doesn't exist
  removeProperty(key) {
    const index = this.properties.findIndex(prop => prop[0] === key);
    if (index !== -1) {
      this.properties.splice(index, 1);
    }
  }

  handleCancel() {
    // if there is no message we go back to choise otherwise we go back to filled
    if (this.giftMessageData.message === '') {
      this.state = 'choice';
    } else {
      this.state = 'filled';
    }
    this.render();
  }

  focusFirstError() {
    const firstError = this.shadowRoot.querySelector('.error-message:not(:empty)');
    if (firstError) {
      firstError.previousElementSibling.focus();
    }
  }

  showSuccessMessage(message) {
    const successElement = document.createElement('div');
    successElement.textContent = message;
    successElement.setAttribute('role', 'status');
    successElement.setAttribute('aria-live', 'polite');
    this.shadowRoot.appendChild(successElement);
    setTimeout(() => successElement.remove(), 5000);
  }

  showErrorMessage(message) {
    const errorElement = document.createElement('div');
    errorElement.textContent = message;
    errorElement.setAttribute('role', 'alert');
    this.shadowRoot.appendChild(errorElement);
    setTimeout(() => errorElement.remove(), 5000);
  }
}

customElements.define('gift-message-form', GiftMessageForm);
